import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';

import { SyncedTripLeg } from '../../../data/models/reservation.model';
import * as providerSlice from '../../../data/provider.slice';
import * as reservationSlice from '../../../data/reservation.slice';

import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Tab,
  Tabs,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PhoneIcon from '@mui/icons-material/Phone';

import SelectMenu from '../../forms/SelectMenu';

import { phoneUtils } from '../../../services/phoneUtils';
import TabPanel from '../../TabPanel';

const PREFIX = 'ReservationTripLegForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  details: `${PREFIX}-details`,
  actions: `${PREFIX}-actions`,
  provider: `${PREFIX}-provider`,
  providerName: `${PREFIX}-providerName`,
  providerContact: `${PREFIX}-providerContact`,
  providerPhone: `${PREFIX}-providerPhone`,
  fieldset: `${PREFIX}-fieldset`,
  fieldLabel: `${PREFIX}-field-label`,
  fieldSelect: `${PREFIX}-fieldSelect`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
  },
  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    gap: theme.spacing(4),
    minWidth: 600,
  },
  [`& .${classes.details}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: 528,
    padding: theme.spacing(4),
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },

  [`& .${classes.providerName}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.providerContact}`]: {
    fontFamily: 'Source Sans Pro',
    fontWeight: 400,
    fontSize: 16,
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.providerPhone}`]: {
    fontFamily: 'Source Sans Pro',
    fontWeight: 400,
    fontSize: 16,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'flex-end',
    gap: 4,
    '& .MuiSvgIcon-root:first-child': {
      marginLeft: 4,
    },
  },

  [`& .${classes.fieldLabel}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.15px',
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.fieldSelect}`]: {
    marginLeft: theme.spacing(4),
  },
}));

interface UpdateStatusFormProps {
  formHook: UseFormReturn<any, any>;
  isSaving: boolean;
  isValid: boolean;
  tripLeg: SyncedTripLeg;
  submit: () => void;
  close: () => void;
}

export default function UpdateStatusForm({
  formHook,
  isSaving,
  isValid,
  tripLeg,
  submit,
  close,
}: UpdateStatusFormProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  // const narrative = useAppSelector(reservationSlice.selectNarrative);

  const [tabIndex, setTabIndex] = useState(0);

  const selectOptions = [
    { value: 'one', label: 'On My Way' },
    { value: 'two', label: 'On Site' },
    { value: 'three', label: 'On Board' },
    { value: 'four', label: 'Completed' },
  ];
  const secondarySelectOptions = [
    { value: 'one', label: 'ETA to On Site' },
    { value: 'two', label: 'ETA to Completed' },
  ];

  const provider = useAppSelector((state) =>
    tripLeg?.transportationProviderID
      ? providerSlice.selectProviderByID(
          state,
          tripLeg.transportationProviderID,
        )
      : undefined,
  );

  const providerContact = provider?.transportationProviderPhones?.[0];
  const providerContactName = `${providerContact?.contactFirstName} ${providerContact?.contactLastName}`;
  const providerContactPhone = phoneUtils.formatPhoneString(
    providerContact?.phoneNumber || '',
    false,
    true,
  );

  const narrative = `<p>Hi, this is [First Name] from American Logistics. Am I speaking with <strong>${providerContactName}</strong>?</p>
  <p>I'm calling regarding trip <strong>${tripLeg.tripLegReferenceID}</strong>. It seems the status in the mobile app isn't updating properly, and we need your help to ensure it's accurate.</p> 
  <p>Can you please follow up with your driver to have them use the app?</p>
  <p>If you're having any issues with the app, try restarting it or your phone. Let me know if you need any help.</p>
  <p>Thanks for your time, and let me know if you need anything else!</p>
  `;

  const handleClose = () => {
    close();
  };

  const handleTabChange = (_evt: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {};

  useEffect(() => {
    if (tripLeg && tripLeg.transportationProviderID) {
      dispatch(
        providerSlice.fetchProviderAsync(tripLeg.transportationProviderID),
      );
    }
  }, [tripLeg]);

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.form}>
        <Box className={classes.provider}>
          <Typography className={classes.providerName}>
            {tripLeg?.transportationProvider?.transportationProviderName || ''}
          </Typography>
          <Typography className={classes.providerContact}>
            {providerContactName || ''}
          </Typography>
          <Typography className={classes.providerPhone}>
            {providerContactPhone || ''}
            <ChatBubbleOutlineIcon fontSize="small" />
            <PhoneIcon fontSize="small" />
          </Typography>
        </Box>

        <Box className={classes.fieldset}>
          <Typography className={classes.fieldLabel}>
            {t('todaysTrips.whatWasTheOutcome')}
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="driver location"
              name="driver-location"
              value={'test'}
              onChange={handleRadioChange}>
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label={t('todaysTrips.noAnswer')}
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label={t('todaysTrips.providerWillUpdate')}
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label={t('todaysTrips.alUpdate')}
              />
              <SelectMenu
                label={t('common.status')}
                name="status"
                values={selectOptions}
                valueAttr="value"
                labelAttr="label"
                required={true}
                control={formHook.control}
                className={classes.fieldSelect}
                // rules={{ required: requiredFieldMessage }}
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label={t('todaysTrips.escalate')}
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label={t('todaysTrips.findNewProvider')}
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box className={classes.actions}>
          <Button color="primary" variant="contained">
            {t('common.submit')}
          </Button>
          <Button color="secondary" variant="contained" onClick={handleClose}>
            {t('common.close')}
          </Button>
        </Box>
      </Paper>
      <Paper elevation={2} className={classes.details}>
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={tabIndex}
          onChange={handleTabChange}
          variant="fullWidth">
          <Tab label={t('reservation.narrative')} />
          <Tab label={t('reservation.details')} />
          <Tab label={t('reservation.activity')} />
        </Tabs>
        <TabPanel value={tabIndex} index={0}>
          <Typography
            dangerouslySetInnerHTML={{
              __html: narrative || '',
            }}></Typography>
        </TabPanel>
        <TabPanel value={tabIndex} index={1}></TabPanel>
        <TabPanel value={tabIndex} index={2}></TabPanel>
      </Paper>
    </StyledBox>
  );
}
